// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui';
import { User as UserIcon } from 'lucide-react';
import { cn } from '@/components/ui';

interface UserTypeaheadProps {
  value: string;
  onValueChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  autoFocus?: boolean;
  /** User IDs that should be flagged as a conflict (rendered greyed-out) */
  conflictUserIds?: string[];
}

export function UserTypeahead({
  value,
  onValueChange,
  suggestions,
  placeholder = 'Enter user ID',
  autoFocus = false,
  conflictUserIds = [],
}: UserTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const conflictSet = React.useMemo(() => new Set(conflictUserIds), [conflictUserIds]);

  const filtered = React.useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return suggestions.slice(0, 50);
    return suggestions.filter(id => id.toLowerCase().includes(q)).slice(0, 50);
  }, [value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      // cm-coding: Close after the user's click completes. Closing on
      // mousedown can move an in-flow dialog action before mouseup, preventing
      // its Cancel or submit click handler from ever running.
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <Input
        id="user-id"
        data-testid="user-typeahead-input"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value.trim());
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="h-[42px] rounded-lg border-[#d1d5db] bg-white px-[13px] py-0 text-[15px] leading-[normal] text-[#0f172a] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] duration-[120ms] focus-visible:border-[#93c5fd] focus-visible:ring-[3px] focus-visible:ring-[#2563eb]/[0.08] md:text-[15px]"
      />
      {open && filtered.length > 0 && (
        // cm-coding: User suggestions must not prevent an administrator from
        // cancelling or submitting the membership dialog. Keep the list in the
        // document flow so it moves the actions below it; an overlaid list can
        // intercept the pointer events intended for those controls.
        <div className="mt-1 max-h-60 w-full overflow-y-auto overscroll-contain rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          {filtered.map((userId) => {
            const isConflict = conflictSet.has(userId);
            return (
              <div
                key={userId}
                data-testid={`user-typeahead-option-${userId}`}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
                  isConflict && 'opacity-60',
                )}
                onClick={() => {
                  onValueChange(userId);
                  setOpen(false);
                }}
              >
                <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1">{userId}</span>
                {isConflict && (
                  <span className="text-xs text-muted-foreground">already added</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
